import { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'hsquest-install-dismissed';

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!deferredPrompt || dismissed) return null;

  const handleInstall = async () => {
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // localStorage unavailable
    }
  };

  return (
    <div
      role="banner"
      aria-label="Install app prompt"
      className="fixed bottom-20 left-4 right-4 z-40 flex items-center gap-3 rounded-xl border px-4 py-3 shadow-lg"
      style={{
        backgroundColor: 'var(--color-theme-surface-bg, #ffffff)',
        borderColor: 'var(--color-theme-surface-border, #e5e7eb)',
      }}
    >
      <Download className="w-5 h-5 shrink-0" style={{ color: 'var(--color-theme-primary, #7c3aed)' }} />
      <p className="flex-1 text-sm font-medium" style={{ color: 'var(--color-theme-text-primary, #1f2937)' }}>
        Add HomeSchool.You to your home screen for the best experience
      </p>
      <button
        onClick={handleInstall}
        aria-label="Install app"
        className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-bold text-white"
        style={{ backgroundColor: 'var(--color-theme-primary, #7c3aed)' }}
      >
        Install
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
        className="shrink-0 text-gray-400 hover:text-gray-600"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
